// 문제은행의 Problem
// 교재문제(WorkbookProblem), 학습지문제(WorksheeetProblem)와 다르다.
// 교재문제, 학습지문제의 키값은 Problem의 키값과 유사하게 만든 것일듯
import { ProblemDomain } from '..'

// private type으로 접근하지 마세요
type _KeypadType = keyof typeof ProblemDomain.KEYPAD_TYPES
type _AnswerUnit = {
  unit: string
  index: number
}
type _AutoScoredType =
  | 'IMPOSSIBLE'
  | 'POSSIBLE_PLUS'
  | 'POSSIBLE_MINUS'
  | 'POSSIBLE_UNIT'
  | 'POSSIBLE_FRACTION'
  | 'POSSIBLE_DECIMAL'
  | 'CALCULATION_FRACTION'
  | 'UNIDENTIFIED'
  | 'POSSIBLE_TEXT'

export namespace Entity {
  export class Problem {
    id: number // 문제 아이디
    conceptId: number // 유형 아이디
    topicId: number | null // 주제 아이디
    subTopicId: number | null // 세부유형 아이디
    subTopicPriority: number | null // 세부유형 내 우선순위
    groupCase: 'CONCEPT' | 'TOPIC' | 'SUB_TOPIC' | 'SUB_TOPIC_SPECIAL' | null // 유사/쌍둥이 추천 타입
    groupCode: number | null // 그룹코드
    /**
     * @deprecated 더 이상 사용하지 않음. 대신 answerType 사용.
     */
    type: ProblemDomain.Type // // 정답 타입
    optionCount: number // 선택지 개수
    level: 1 | 2 | 3 | 4 | 5 // 난이도
    levelOfConceptChip: 'A' | 'B' | 'C' // 유형칩
    problemImageUrl: string // 문제 이미지 URL
    answerImageUrl: string // 정답 이미지 URL
    solutionImageUrl: string // 해설 이미지 URL
    answer: string // 정답

    answerCount: number // 정답 개수

    autoScoredType: _AutoScoredType // 자동채점 타입
    autoScored: boolean // 자동채점 가능한 문제인가?

    hidden: boolean // 숨겨져 있는가?
    trendy: boolean // 신경향 문제인가?
    sample: boolean // 샘플 문제인가?
    actualSize: boolean // 실측이 가능해야하는 문제인가?

    // 문제 풀이 동영상
    video?: {
      id: number | null
      videoUrl: string | null
      thumbnailUrl: string | null
      subtitleUrl?: string | null
    } | null
    videoMatched?: boolean // 문제 풀이 동영상이 정확히 해당 문제에 대한 동영상인가?
    practiceProblemYear: number | null // 수능/모의고사 문제인 경우 시행된 연도

    problemSummary: {
      problemId: number
      totalUsed: number
      correctTimes: number
      wrongTimes: number
      answerRate: number
    }

    answerType: ProblemDomain.Type
    answerUnits: readonly _AnswerUnit[] | null // a.k.a answerUnits
    keypadTypes: readonly _KeypadType[] | null // a.k.a keypadTypes
    conceptName?: string | null

    // tagTop은 추후에는 worksheetProblem으로 옮겨질 것. 기존 학습지에는 포함되기 때문에 optional로 일단 작성.
    tagTop?: string | null

    constructor(params: Problem) {
      this.id = params.id
      this.conceptId = params.conceptId
      this.groupCase = params.groupCase
      this.groupCode = params.groupCode
      this.topicId = params.topicId
      this.subTopicId = params.subTopicId
      this.subTopicPriority = params.subTopicPriority
      this.type = params.type
      this.answerType = params.answerType
      this.answerCount = params.answerCount
      this.optionCount = params.optionCount
      this.level = params.level
      this.levelOfConceptChip = params.levelOfConceptChip
      this.problemImageUrl = params.problemImageUrl
      this.answerImageUrl = params.answerImageUrl
      this.solutionImageUrl = params.solutionImageUrl
      this.answer = params.answer
      this.autoScoredType = params.autoScoredType
      this.hidden = params.hidden
      this.trendy = params.trendy
      this.sample = params.sample
      this.actualSize = params.actualSize
      this.tagTop = params.tagTop || null
      this.answerUnits = params.answerUnits
      this.autoScored = params.autoScored
      this.keypadTypes = params.keypadTypes
      this.problemSummary = params.problemSummary
      this.conceptName = params.conceptName || null
      this.video = params.video || { id: null, videoUrl: null, thumbnailUrl: null }
      this.videoMatched = params.videoMatched
      this.practiceProblemYear = params.practiceProblemYear
    }

    static getKoLevel = (level: Problem['level']) => {
      switch (level) {
        case 1:
          return '하'
        case 2:
          return '중하'
        case 3:
          return '중'
        case 4:
          return '상'
        case 5:
          return '최상'
      }
    }

    static getKoAnswerType = (type: Problem['answerType']) => {
      switch (type) {
        case 'SINGLE_CHOICE':
          return '객관식'
        case 'SHORT_ANSWER':
          return '주관식'
        case 'MULTIPLE_CHOICE':
          return '선다형'
        default:
          return ''
      }
    }

    static getTagTopList = (tagTop: Problem['tagTop'] | undefined) => {
      if (!tagTop) return []

      return tagTop.split('\n').map((tagTop) => {
        const type = Problem._parseTagTop(tagTop)
        const cuttedTagTop = Problem._removePrefix(tagTop)

        return {
          type,
          cuttedTagTop,
        }
      })
    }

    private static _parseTagTop(tagTop: string) {
      let type: string | undefined

      if (tagTop?.includes(ProblemDomain.TAG_TOP_TYPE.twin)) type = 'twin'
      if (tagTop?.includes(ProblemDomain.TAG_TOP_TYPE.similar)) type = 'similar'
      if (tagTop?.includes(ProblemDomain.TAG_TOP_TYPE.wrong)) type = 'wrong'
      if (tagTop?.includes(ProblemDomain.TAG_TOP_TYPE.same)) type = 'same'

      return type
    }

    /***
     * @example this.tagTop이 "[쌍둥이] 소수와 분수"일때 cuttedTagTop은 "소수와 분수"
     ***/
    private static _removePrefix(tagTop: string) {
      const target = tagTop.indexOf(']')
      const hasTargetName = target > -1

      if (!hasTargetName) return null

      return tagTop.slice(target + 1)
    }
  }
}
